//
// Copyright 2020 DXOS.org
//

import { inspect } from 'node:util';

import { inspectObject, raise } from '@dxos/debug';

export type Primitive = string | number | boolean | null | undefined;

export type PrimitiveProjection<T> = (value: T) => Primitive;

const MAX_SERIALIZATION_LENGTH = 10;

/**
 * A set implementation that can hold complex values (like Buffer).
 *
 * The user must provide a projection function which returns a primitive
 * representation of the complex value. This function must be 1-to-1 mapping.
 *
 * Look at `../complex.test.ts` for usage examples.
 */
export class ComplexSet<T> implements Set<T> {
  private readonly _values = new Map<Primitive, T>();

  // prettier-ignore
  constructor(
    private readonly _projection: PrimitiveProjection<T>,
    values?: Iterable<T> | null,
  ) {
    if (values) {
      for (const value of values) {
        this.add(value);
      }
    }
  }

  toString(): string {
    return inspectObject(this);
  }

  toJSON(): T[] | { size: number } {
    return this._values.size > MAX_SERIALIZATION_LENGTH
      ? { size: this._values.size }
      : Array.from(this._values.values());
  }

  [inspect.custom](): string {
    return inspectObject(this);
  }

  add(value: T): this {
    this._values.set(this._projection(value), value);
    return this;
  }

  clear(): void {
    this._values.clear();
  }

  delete(value: T): boolean {
    return this._values.delete(this._projection(value));
  }

  forEach(callbackfn: (value: T, value2: T, set: Set<T>) => void, thisArg?: any): void {
    if (thisArg) {
      callbackfn = callbackfn.bind(thisArg);
    }

    this._values.forEach((value) => callbackfn(value, value, this));
  }

  has(value: T): boolean {
    return this._values.has(this._projection(value));
  }

  get size(): number {
    return this._values.size;
  }

  [Symbol.iterator](): SetIterator<T> {
    return this._values.values();
  }

  *entries(): SetIterator<[T, T]> {
    for (const value of this._values.values()) {
      yield [value, value];
    }
  }

  keys(): SetIterator<T> {
    return this[Symbol.iterator]();
  }

  values(): SetIterator<T> {
    return this[Symbol.iterator]();
  }

  get [Symbol.toStringTag](): string {
    return 'ComplexSet';
  }

  union<U>(other: ReadonlySetLike<U>): Set<T | U> {
    throw new Error('Method not implemented.');
  }

  intersection<U>(other: ReadonlySetLike<U>): Set<T & U> {
    throw new Error('Method not implemented.');
  }

  difference<U>(other: ReadonlySetLike<U>): Set<T> {
    throw new Error('Method not implemented.');
  }

  symmetricDifference<U>(other: ReadonlySetLike<U>): Set<T | U> {
    throw new Error('Method not implemented.');
  }

  isSubsetOf(other: ReadonlySetLike<unknown>): boolean {
    throw new Error('Method not implemented.');
  }

  isSupersetOf(other: ReadonlySetLike<unknown>): boolean {
    throw new Error('Method not implemented.');
  }

  isDisjointFrom(other: ReadonlySetLike<unknown>): boolean {
    throw new Error('Method not implemented.');
  }
}

export type ComplexSetConstructor<T> = new (values?: Iterable<T> | null) => ComplexSet<T>;

/**
 * Create a subclass of ComplexSet with predefined projection function.
 */
export const makeSet = <T>(projection: PrimitiveProjection<T>): ComplexSetConstructor<T> => {
  return class BoundComplexSet extends ComplexSet<T> {
    constructor(values?: Iterable<T> | null) {
      super(projection, values);
    }
  };
};

/**
 * A map implementation that can hold complex values (like Buffer) as keys.
 * The user must provide a projection function for map keys which returns a primitive
 * representation of the complex value. This function must be 1-to-1 mapping.
 * Look at `../complex.test.ts` for usage examples.
 */
export class ComplexMap<K, V> implements Map<K, V> {
  private readonly _keys = new Map<Primitive, K>();
  private readonly _values = new Map<Primitive, V>();

  // prettier-ignore
  constructor(
    private readonly _keyProjection: PrimitiveProjection<K>,
    entries?: readonly (readonly [K, V])[] | null,
  ) {
    if (entries) {
      for (const [key, value] of entries) {
        this.set(key, value);
      }
    }
  }

  toString(): string {
    return inspectObject(this);
  }

  toJSON(): V[] | { size: number } {
    return this._values.size > MAX_SERIALIZATION_LENGTH
      ? { size: this._values.size }
      : Array.from(this._values.values());
  }

  [inspect.custom](): string {
    return inspectObject(this);
  }

  clear(): void {
    this._keys.clear();
    this._values.clear();
  }

  delete(key: K): boolean {
    const keyDeleted = this._keys.delete(this._keyProjection(key));
    const valueDeleted = this._values.delete(this._keyProjection(key));
    return keyDeleted || valueDeleted;
  }

  forEach(callbackfn: (value: V, key: K, map: Map<K, V>) => void, thisArg?: any): void {
    if (thisArg) {
      callbackfn = callbackfn.bind(thisArg);
    }

    this._keys.forEach((key, primitive) =>
      callbackfn(this._values.get(primitive) ?? raise(new Error('Map corrupted.')), key, this),
    );
  }

  get(key: K): V | undefined {
    return this._values.get(this._keyProjection(key));
  }

  has(key: K): boolean {
    return this._keys.has(this._keyProjection(key));
  }

  set(key: K, value: V): this {
    const primitive = this._keyProjection(key);
    this._keys.set(primitive, key);
    this._values.set(primitive, value);
    return this;
  }

  /**
   * Returns the value for the given key if present, otherwise inserts and returns the default value.
   * @param key - The key to look up or insert.
   * @param defaultValue - The value to insert if the key is not present.
   * @returns The existing or newly inserted value.
   */
  getOrInsert(key: K, defaultValue: V): V {
    const primitive = this._keyProjection(key);
    if (this._values.has(primitive)) {
      return this._values.get(primitive)!;
    }
    this.set(key, defaultValue);
    return defaultValue;
  }

  /**
   * Returns the value for the given key if present, otherwise computes, inserts, and returns a new value.
   * The callback is only invoked when the key is missing.
   * @param key - The key to look up or insert.
   * @param callbackfn - Function to compute the value if the key is not present.
   * @returns The existing or newly computed value.
   */
  getOrInsertComputed(key: K, callbackfn: (key: K) => V): V {
    const primitive = this._keyProjection(key);
    if (this._values.has(primitive)) {
      return this._values.get(primitive)!;
    }
    const value = callbackfn(key);
    this.set(key, value);
    return value;
  }

  get size(): number {
    return this._keys.size;
  }

  *[Symbol.iterator](): SetIterator<[K, V]> {
    for (const [primitive, key] of this._keys) {
      const value = this._values.get(primitive) ?? raise(new Error('Map corrupted.'));
      yield [key, value];
    }
  }

  entries(): SetIterator<[K, V]> {
    return this[Symbol.iterator]();
  }

  keys(): SetIterator<K> {
    return this._keys.values();
  }

  values(): SetIterator<V> {
    return this._values.values();
  }

  mapValues<R>(mapper: (v: V, k: K) => R): ComplexMap<K, R> {
    return new ComplexMap(
      this._keyProjection,
      [...this.entries()].map(([key, value]) => [key, mapper(value, key)]),
    );
  }

  get [Symbol.toStringTag](): string {
    return 'ComplexMap';
  }
}

export type ComplexMapConstructor<K> = new <V>(entries?: readonly (readonly [K, V])[] | null) => ComplexMap<K, V>;

/**
 * Create a subclass of ComplexMap with predefined key projection function.
 */
export const makeMap = <K>(keyProjection: PrimitiveProjection<K>): ComplexMapConstructor<K> =>
  class BoundComplexMap<V> extends ComplexMap<K, V> {
    constructor(entries?: readonly (readonly [K, V])[] | null) {
      super(keyProjection, entries);
    }
  };
